import { useState, useEffect } from 'react';
import { X, Mail, UserPlus, Loader2, Copy, CheckCircle2, MessageSquare, Eye, Send } from 'lucide-react';
import { useAppStore } from '@/lib/store';
import { API_URL } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';

interface BetaReaderInvite {
    id: string;
    reader_email: string;
    reader_pseudonym: string | null;
    status: 'pending' | 'accepted' | 'declined' | 'expired';
    permissions: string;
    message: string | null;
    created_at: string;
    expires_at: string;
}

interface BetaReaderComment {
    id: string;
    chapter_id: string;
    reader_pseudonym: string;
    selected_text: string;
    comment: string;
    status: 'open' | 'resolved' | 'dismissed';
    created_at: string;
}

interface BetaReaderModalProps {
    novelId: string;
    isOpen: boolean;
    onClose: () => void;
}

export function BetaReaderModal({ novelId, isOpen, onClose }: BetaReaderModalProps) {
    const { themeProfile, novels, activeNovelId } = useAppStore();
    const [activeTab, setActiveTab] = useState<'invite' | 'manage' | 'comments'>('invite');
    const [invites, setInvites] = useState<BetaReaderInvite[]>([]);
    const [comments, setComments] = useState<BetaReaderComment[]>([]);
    
    // Invite form
    const [email, setEmail] = useState('');
    const [message, setMessage] = useState('');
    const [permissions, setPermissions] = useState('read,comment');
    const [isSending, setIsSending] = useState(false);
    const [inviteLink, setInviteLink] = useState('');
    const [copied, setCopied] = useState(false);

    const activeNovel = novels.find(n => n.id === activeNovelId);

    useEffect(() => {
        if (isOpen && novelId) {
            fetchInvites();
            fetchComments();
        }
    }, [isOpen, novelId]);

    const fetchInvites = async () => {
        const token = localStorage.getItem('auth_token');
        try {
            const res = await fetch(`${API_URL}/api/novels/${novelId}/beta-readers`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.invites) setInvites(data.invites);
        } catch (e) {
            console.error('Failed to fetch invites:', e);
        }
    };

    const fetchComments = async () => {
        const token = localStorage.getItem('auth_token');
        try {
            const res = await fetch(`${API_URL}/api/novels/${novelId}/beta-comments`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await res.json();
            if (data.comments) setComments(data.comments);
        } catch (e) {
            console.error('Failed to fetch comments:', e);
        }
    };

    const handleSendInvite = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email) return;
        
        setIsSending(true);
        const token = localStorage.getItem('auth_token');
        
        try {
            const res = await fetch(`${API_URL}/api/novels/${novelId}/beta-readers/invite`, {
                method: 'POST',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ email, message, permissions })
            });
            
            const data = await res.json();
            if (data.success) {
                const link = `${window.location.origin}/beta-read/${data.inviteToken}`;
                setInviteLink(link);
                fetchInvites();
                setEmail('');
                setMessage('');
            }
        } catch (e) {
            console.error('Failed to send invite:', e);
        } finally {
            setIsSending(false);
        }
    };

    const copyLink = () => {
        navigator.clipboard.writeText(inviteLink);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'accepted': return 'text-green-600 bg-green-100';
            case 'declined': return 'text-red-600 bg-red-100';
            case 'expired': return 'text-gray-600 bg-gray-100';
            default: return 'text-amber-600 bg-amber-100';
        }
    };

    const getCommentStatusColor = (status: string) => {
        switch (status) {
            case 'resolved': return 'text-green-600 bg-green-100';
            case 'dismissed': return 'text-gray-600 bg-gray-100';
            default: return 'text-blue-600 bg-blue-100';
        }
    };

    const updateCommentStatus = async (commentId: string, status: string) => {
        const token = localStorage.getItem('auth_token');
        try {
            const res = await fetch(`${API_URL}/api/beta-comments/${commentId}`, {
                method: 'PATCH',
                headers: { 
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ status })
            });
            
            if (res.ok) {
                fetchComments();
            }
        } catch (e) {
            console.error('Failed to update comment:', e);
        }
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
                onClick={onClose}
            >
                <motion.div
                    initial={{ scale: 0.95, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.95, opacity: 0 }}
                    onClick={(e) => e.stopPropagation()}
                    className="w-full max-w-2xl max-h-[85vh] overflow-hidden rounded-3xl border shadow-2xl flex flex-col"
                    style={{ backgroundColor: themeProfile.paperSurface, borderColor: themeProfile.border }}
                >
                    {/* Header */}
                    <div 
                        className="px-6 py-4 flex items-center justify-between border-b"
                        style={{ borderColor: themeProfile.border }}
                    >
                        <div>
                            <h2 className="text-xl font-bold" style={{ color: themeProfile.text }}>
                                Beta Readers
                            </h2>
                            <p className="text-xs opacity-60">
                                {activeNovel?.title} • Invite readers to get feedback
                            </p>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 rounded-lg hover:bg-black/5 transition-colors"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Tabs */}
                    <div className="flex border-b" style={{ borderColor: themeProfile.border }}>
                        {[
                            { id: 'invite', label: 'Invite', icon: UserPlus },
                            { id: 'manage', label: 'Manage', icon: Eye, count: invites.length },
                            { id: 'comments', label: 'Feedback', icon: MessageSquare, count: comments.filter(c => c.status === 'open').length }
                        ].map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id as any)}
                                className={cn(
                                    "flex items-center gap-2 px-6 py-3 text-sm font-medium transition-all flex-1 justify-center border-b-2",
                                    activeTab === tab.id
                                        ? "border-amber-500 text-amber-600"
                                        : "border-transparent opacity-50 hover:opacity-100"
                                )}
                            >
                                <tab.icon className="w-4 h-4" />
                                {tab.label}
                                {tab.count && tab.count > 0 && (
                                    <span className="px-1.5 py-0.5 rounded-full text-[10px] bg-amber-100 text-amber-700">
                                        {tab.count}
                                    </span>
                                )}
                            </button>
                        ))}
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto p-6">
                        {activeTab === 'invite' && (
                            <div className="space-y-6">
                                {inviteLink ? (
                                    <motion.div
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="p-6 rounded-2xl border text-center space-y-4"
                                        style={{ borderColor: themeProfile.border, backgroundColor: themeProfile.background }}
                                    >
                                        <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                                            <CheckCircle2 className="w-8 h-8 text-green-600" />
                                        </div>
                                        <div>
                                            <h3 className="font-bold text-lg" style={{ color: themeProfile.text }}>
                                                Invite Sent!
                                            </h3>
                                            <p className="text-sm opacity-60">
                                                Share this link with your beta reader
                                            </p>
                                        </div>
                                        <div className="flex gap-2">
                                            <input
                                                type="text"
                                                value={inviteLink}
                                                readOnly
                                                className="flex-1 px-4 py-3 rounded-xl border bg-transparent text-sm"
                                                style={{ borderColor: themeProfile.border }}
                                            />
                                            <button
                                                onClick={copyLink}
                                                className="px-4 py-3 rounded-xl font-medium text-sm transition-all hover:scale-105"
                                                style={{ backgroundColor: themeProfile.accent, color: themeProfile.background }}
                                            >
                                                {copied ? <CheckCircle2 className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                                            </button>
                                        </div>
                                        <button
                                            onClick={() => setInviteLink('')}
                                            className="text-sm opacity-60 hover:opacity-100 transition-opacity"
                                        >
                                            Send another invite
                                        </button>
                                    </motion.div>
                                ) : (
                                    <form onSubmit={handleSendInvite} className="space-y-5">
                                        <div className="space-y-2">
                                            <label className="text-xs font-bold uppercase tracking-wider opacity-60">
                                                Reader's Email
                                            </label>
                                            <div className="relative">
                                                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 opacity-40" />
                                                <input
                                                    type="email"
                                                    value={email}
                                                    onChange={(e) => setEmail(e.target.value)}
                                                    required
                                                    placeholder="reader@example.com"
                                                    className="w-full pl-12 pr-4 py-3 rounded-xl border bg-transparent focus:outline-none focus:ring-2 focus:ring-amber-500/20"
                                                    style={{ borderColor: themeProfile.border }}
                                                />
                                            </div>
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-xs font-bold uppercase tracking-wider opacity-60">
                                                Personal Message (optional)
                                            </label>
                                            <textarea
                                                value={message}
                                                onChange={(e) => setMessage(e.target.value)}
                                                rows={3}
                                                placeholder="Hi! I'd love your feedback on my novel..."
                                                className="w-full px-4 py-3 rounded-xl border bg-transparent focus:outline-none focus:ring-2 focus:ring-amber-500/20 resize-none"
                                                style={{ borderColor: themeProfile.border }}
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-xs font-bold uppercase tracking-wider opacity-60">
                                                Permissions
                                            </label>
                                            <div className="flex gap-3">
                                                {[
                                                    { value: 'read', label: 'Read Only', icon: Eye },
                                                    { value: 'read,comment', label: 'Read & Comment', icon: MessageSquare },
                                                ].map((option) => (
                                                    <button
                                                        key={option.value}
                                                        type="button"
                                                        onClick={() => setPermissions(option.value)}
                                                        className={cn(
                                                            "flex items-center gap-2 px-4 py-3 rounded-xl border transition-all flex-1",
                                                            permissions === option.value
                                                                ? "border-amber-500 bg-amber-50"
                                                                : "opacity-60 hover:opacity-100"
                                                        )}
                                                        style={{ borderColor: permissions === option.value ? themeProfile.accent : themeProfile.border }}
                                                    >
                                                        <option.icon className="w-4 h-4" />
                                                        <span className="text-sm font-medium">{option.label}</span>
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        <button
                                            type="submit"
                                            disabled={isSending}
                                            className="w-full py-4 rounded-xl font-bold text-white transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
                                            style={{ backgroundColor: themeProfile.accent }}
                                        >
                                            {isSending ? (
                                                <Loader2 className="w-5 h-5 animate-spin" />
                                            ) : (
                                                <Send className="w-5 h-5" />
                                            )}
                                            Send Invitation
                                        </button>
                                    </form>
                                )}
                            </div>
                        )}

                        {activeTab === 'manage' && (
                            <div className="space-y-3">
                                {invites.map((invite) => (
                                    <motion.div
                                        key={invite.id}
                                        initial={{ opacity: 0, x: -10 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        className="p-4 rounded-xl border flex items-center justify-between"
                                        style={{ backgroundColor: themeProfile.background, borderColor: themeProfile.border }}
                                    >
                                        <div>
                                            <p className="font-medium" style={{ color: themeProfile.text }}>
                                                {invite.reader_pseudonym || invite.reader_email}
                                            </p>
                                            <p className="text-xs opacity-50">
                                                Invited {new Date(invite.created_at).toLocaleDateString()}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className={cn(
                                                "px-2.5 py-1 rounded-full text-[10px] font-bold uppercase",
                                                getStatusColor(invite.status)
                                            )}>
                                                {invite.status}
                                            </span>
                                        </div>
                                    </motion.div>
                                ))}

                                {invites.length === 0 && (
                                    <div className="text-center py-12 opacity-40">
                                        <Eye className="w-12 h-12 mx-auto mb-4" />
                                        <p>No beta readers yet.</p>
                                        <p className="text-sm">Invite someone to get started!</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {activeTab === 'comments' && (
                            <div className="space-y-4">
                                {comments.map((comment) => (
                                    <motion.div
                                        key={comment.id}
                                        initial={{ opacity: 0, y: 10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="p-4 rounded-xl border"
                                        style={{ backgroundColor: themeProfile.background, borderColor: themeProfile.border }}
                                    >
                                        <div className="flex items-start justify-between mb-3">
                                            <div className="flex items-center gap-2">
                                                <div 
                                                    className="w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold"
                                                    style={{ backgroundColor: themeProfile.accent, color: themeProfile.background }}
                                                >
                                                    {comment.reader_pseudonym[0].toUpperCase()}
                                                </div>
                                                <span className="font-medium text-sm">{comment.reader_pseudonym}</span>
                                            </div>
                                            <span className={cn(
                                                "px-2 py-0.5 rounded-full text-[10px] font-bold uppercase",
                                                getCommentStatusColor(comment.status)
                                            )}>
                                                {comment.status}
                                            </span>
                                        </div>
                                        
                                        {comment.selected_text && (
                                            <blockquote className="text-sm italic opacity-70 mb-3 pl-3 border-l-2" style={{ borderColor: themeProfile.accent }}>
                                                "{comment.selected_text}"
                                            </blockquote>
                                        )}
                                        
                                        <p className="text-sm mb-4" style={{ color: themeProfile.text }}>
                                            {comment.comment}
                                        </p>

                                        {comment.status === 'open' && (
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={() => updateCommentStatus(comment.id, 'resolved')}
                                                    className="px-3 py-1.5 rounded-lg text-xs font-medium bg-green-100 text-green-700 hover:bg-green-200 transition-colors"
                                                >
                                                    Mark Resolved
                                                </button>
                                                <button
                                                    onClick={() => updateCommentStatus(comment.id, 'dismissed')}
                                                    className="px-3 py-1.5 rounded-lg text-xs font-medium hover:bg-black/5 transition-colors opacity-60"
                                                >
                                                    Dismiss
                                                </button>
                                            </div>
                                        )}
                                    </motion.div>
                                ))}

                                {comments.length === 0 && (
                                    <div className="text-center py-12 opacity-40">
                                        <MessageSquare className="w-12 h-12 mx-auto mb-4" />
                                        <p>No feedback yet.</p>
                                        <p className="text-sm">Comments from beta readers will appear here.</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}
